import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const Toast = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'success', // success, error, warning, info
  autoClose = true,
  duration = 3000
}) => {
  useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, duration, onClose]);

  if (!isOpen) return null;

  const configs = {
    success: {
      bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
      icon: CheckCircle,
      iconBg: 'bg-white/20'
    },
    error: {
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      icon: XCircle,
      iconBg: 'bg-white/20'
    },
    warning: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
      icon: AlertCircle,
      iconBg: 'bg-white/20'
    },
    info: {
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      icon: Info,
      iconBg: 'bg-white/20'
    }
  };

  const config = configs[type] || configs.success;
  const Icon = config.icon;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <div className={`${config.bg} rounded-xl shadow-2xl p-4 min-w-[320px] max-w-md`}>
        <div className="flex items-start">
          <div className={`${config.iconBg} rounded-full p-2 mr-3`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-semibold">{title}</h4>
            {message && (
              <p className="text-white/90 text-sm mt-1">{message}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-3 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal version for more prominent notifications
export const ToastModal = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'success'
}) => {
  if (!isOpen) return null;

  const configs = {
    success: {
      bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
      btnBg: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
      icon: CheckCircle
    },
    error: {
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      btnBg: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
      icon: XCircle
    },
    warning: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
      btnBg: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
      icon: AlertCircle
    },
    info: {
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      btnBg: 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
      icon: Info
    }
  };

  const config = configs[type] || configs.success;
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl animate-bounce-in">
        {/* Header */}
        <div className={`${config.bg} px-6 py-6 text-center`}>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>

        {/* Body */}
        <div className="p-6 text-center">
          <p className="text-gray-600 mb-6">{message}</p>
          <button
            onClick={onClose}
            className={`w-full ${config.btnBg} text-white py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast;
